import { Badge, Box, Divider, Stack, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ArrowDown2, Notification } from "iconsax-react";
import { Link, useRouterState } from "@tanstack/react-router";
import { memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { SefanoIconButton } from "@/components/Atoms";
import { PRIMARY_NAV_ITEMS } from "@/lib/navigation/navItems";
import { ShopSwitcher } from "../ShopSwitcher";
import { UserAccountPopover } from "../UserAccountPopover";
import type { AppNavbarProps } from "./AppNavbar.types";
import * as styles from "./AppNavbar.styles";

export type { AppNavbarProps } from "./AppNavbar.types";

function isNavActive(path: string, activePath: string): boolean {
  if (path === "/") return activePath === "/";
  return activePath.startsWith(path);
}

export const AppNavbar = memo(function AppNavbar({
  notificationCount = 0,
  storeName,
  userName,
  userRole,
  storeRoles = {},
  stores = [],
  activeStoreId = null,
  onSelectStore,
  activePath: activePathProp,
}: AppNavbarProps) {
  const { t } = useTranslation("common");
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const routerState = useRouterState();
  const activePath = activePathProp ?? routerState.location.pathname;
  const resolvedStoreName = storeName?.trim() || t("notFound");
  const resolvedUserName = userName?.trim() || resolvedStoreName;
  const resolvedUserRole = userRole?.trim() || t("navbar.storeManager");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);

  const navItems = useMemo(
    () =>
      PRIMARY_NAV_ITEMS.filter(
        (item) => !item.mobileOnly && (!item.desktopOnly || isLargeScreen),
      ).map((item) => ({
        ...item,
        active: isNavActive(item.to, activePath),
      })),
    [activePath, isLargeScreen],
  );

  const handleUserMenuToggle = useCallback(() => {
    setUserMenuOpen((prev) => !prev);
    setShopMenuOpen(false);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuOpen(false);
  }, []);

  const handleShopMenuOpenChange = useCallback((open: boolean) => {
    setShopMenuOpen(open);
    if (open) setUserMenuOpen(false);
  }, []);

  const handleCloseMenus = useCallback(() => {
    setUserMenuOpen(false);
    setShopMenuOpen(false);
  }, []);

  const userInitial = resolvedUserName.charAt(0);
  const anyMenuOpen = userMenuOpen || shopMenuOpen;

  return (
    <>
      {anyMenuOpen ? (
        <Box sx={styles.menuBackdrop} onClick={handleCloseMenus} aria-hidden />
      ) : null}

      <Stack component="header" direction="row" sx={styles.root}>
        <Stack direction="row" alignItems="center" sx={styles.startCluster}>
          <Box component="img" src="/images/nav_logo.png" alt="Safano" sx={styles.logo} />
          {stores.length > 0 && onSelectStore ? (
            <ShopSwitcher
              stores={stores}
              activeStoreId={activeStoreId}
              onSelectStore={onSelectStore}
              storeRoles={storeRoles}
              onOpenChange={handleShopMenuOpenChange}
            />
          ) : null}
          <Stack component="nav" direction="row" sx={styles.nav}>
            {navItems.map((item) => (
              <Box
                key={item.to}
                component={Link}
                to={item.to}
                sx={styles.navLink(item.active)}
              >
                {t(item.labelKey)}
              </Box>
            ))}
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" sx={styles.endCluster}>
          <Badge
            variant="dot"
            color="error"
            overlap="circular"
            invisible={notificationCount <= 0}
            sx={styles.badge}
          >
            <SefanoIconButton variant="plain" aria-label={t("navbar.notificationsLabel")}>
              <Notification size={20} variant="Linear" />
            </SefanoIconButton>
          </Badge>

          <Divider orientation="vertical" flexItem sx={styles.divider} />

          <Box sx={styles.userMenuWrap}>
            <Box component="button" type="button" sx={styles.userPill} onClick={handleUserMenuToggle}>
              <Box sx={styles.avatar}>{userInitial}</Box>
              <Stack sx={styles.userText}>
                <Typography variant="body2" fontWeight={600} sx={styles.userName}>
                  {resolvedUserName}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={styles.userRole}>
                  {t("navbar.userRoleInStore", { role: resolvedUserRole })}
                </Typography>
              </Stack>
              <Box sx={styles.userChevron}>
                <ArrowDown2
                  size={14}
                  variant="Linear"
                  color={theme.palette.text.secondary}
                />
              </Box>
            </Box>

            <UserAccountPopover open={userMenuOpen} onClose={handleUserMenuClose} />
          </Box>
        </Stack>
      </Stack>
    </>
  );
});
